import { DataProvider, fetchUtils } from 'react-admin';
import dataProvider, { flattenFilterParams } from './dataProvider';
import ssoCrudDataProvider from './ssoCrudDataProvider';

const ssoBaseUrl = process.env.REACT_APP_SSO_URL || 'https://oa-sso.wsk.gmbh';
const ssoUrl = `${ssoBaseUrl}/api`;

const httpClient = (url: string, options: any = {}) => {
    const token = localStorage.getItem('token');
    if (!options.headers) {
        options.headers = new Headers({
            Accept: 'application/ld+json',
        });
    }
    if (token) {
        options.headers.set('Authorization', `Bearer ${token}`);
    }
    if (options.method === 'POST') {
        options.headers.set('Content-Type', 'application/ld+json');
    } else if (options.method === 'PATCH') {
        options.headers.set('Content-Type', 'application/merge-patch+json');
    } else if (options.method === 'PUT') {
        options.headers.set('Content-Type', 'application/ld+json');
    }
    return fetchUtils.fetchJson(url, options);
};

// Resources that should go to SSO instead of API
const ssoResources = ['profile', 'users'];
const ssoListResources = ['users', 'departments', 'user_groups'];

// Voll-CRUD-Resources am SSO (Hydra/IRI-Konvertierung im ssoCrudDataProvider) —
// seit 2026-09 haendelt die App die online:academy-Einladungen selbst
// (azubi:web-Feedback); companies/roles braucht das Einladungsformular als
// Referenzen.
const ssoCrudResources = ['invitations', 'companies', 'roles'];

const ssoDataProvider: DataProvider = {
    ...dataProvider,

    create: (resource, params) => {
        if (ssoCrudResources.includes(resource)) {
            return ssoCrudDataProvider.create(resource, params);
        }
        return dataProvider.create(resource, params);
    },

    delete: (resource, params) => {
        if (ssoCrudResources.includes(resource)) {
            return ssoCrudDataProvider.delete(resource, params);
        }
        return dataProvider.delete(resource, params);
    },

    deleteMany: (resource, params) => {
        if (ssoCrudResources.includes(resource)) {
            return ssoCrudDataProvider.deleteMany(resource, params);
        }
        return dataProvider.deleteMany(resource, params);
    },

    getList: (resource, params) => {
        if (ssoCrudResources.includes(resource)) {
            return ssoCrudDataProvider.getList(resource, params);
        }
        if (ssoListResources.includes(resource)) {
            const page = params.pagination?.page || 1;
            const perPage = params.pagination?.perPage || 10;
            const sortField = params.sort?.field || 'id';
            const sortOrder = params.sort?.order || 'ASC';

            const query: Record<string, string> = {
                [`order[${sortField}]`]: sortOrder,
                page: page.toString(),
                itemsPerPage: perPage.toString(),
                ...flattenFilterParams(params.filter),
            };

            const url = `${ssoUrl}/${resource}?${new URLSearchParams(query).toString()}`;

            return httpClient(url).then(({ json }) => {
                const members = json['hydra:member'] || json.member || [];
                return {
                    data: members.map((user: any) => ({
                        ...user,
                        id: user.id || user['@id']
                    })),
                    total: json['hydra:totalItems'] || json.totalItems || 0,
                };
            });
        }
        return dataProvider.getList(resource, params);
    },

    getOne: (resource, params) => {
        if (ssoCrudResources.includes(resource)) {
            return ssoCrudDataProvider.getOne(resource, params);
        }
        if (resource === 'profile') {
            // For profile, fetch from SSO /me endpoint
            return httpClient(`${ssoUrl}/me`).then(({ json }) => ({
                data: { ...json, id: json.id || json.sub },
            }));
        }
        if (resource === 'users') {
            // Fetch single user from SSO
            return httpClient(`${ssoUrl}/${resource}/${params.id}`).then(({ json }) => ({
                data: { ...json, id: json.id || json['@id'] },
            }));
        }
        return dataProvider.getOne(resource, params);
    },

    getMany: (resource, params) => {
        if (ssoCrudResources.includes(resource)) {
            return ssoCrudDataProvider.getMany(resource, params);
        }
        if (resource === 'users') {
            return Promise.all(
                params.ids.map(id =>
                    httpClient(`${ssoUrl}/${resource}/${id}`)
                        .then(({ json }) => ({
                            ...json,
                            id: json.id || json['@id']
                        }))
                        .catch(() => null)
                )
            ).then(results => ({
                data: results.filter(user => user !== null)
            }));
        }
        return dataProvider.getMany(resource, params);
    },

    update: (resource, params) => {
        if (ssoCrudResources.includes(resource)) {
            return ssoCrudDataProvider.update(resource, params);
        }
        if (resource === 'profile') {
            // For profile, update via SSO /me endpoint
            const data = { ...params.data };
            delete data.id;
            delete data['@id'];
            delete data['@type'];
            delete data['@context'];
            delete data.roles;

            return httpClient(`${ssoUrl}/me`, {
                method: 'PATCH',
                body: JSON.stringify(data),
            }).then(({ json }) => ({
                data: { ...json, id: json.id || json.sub },
            }));
        }
        return dataProvider.update(resource, params);
    },
};

export default ssoDataProvider;
